
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { toast } from '@/hooks/use-toast';
import { useOrganization } from '@/contexts/OrganizationContext';

export interface Contact {
  id: string;
  name: string;
  email: string;
  phone_number?: string;
  company_name?: string;
  designation?: string;
  contact_type: 'Marketing' | 'Finance' | 'Legal' | 'Logistics' | 'Vendor' | 'Other';
  linked_brand_ids?: string[];
  linked_campaign_ids?: string[];
  organization_id?: string;
  created_by?: string;
  created_at: string;
  updated_at: string;
}

export interface ContactFormData {
  name: string;
  email: string;
  phone_number?: string;
  company_name?: string;
  designation?: string;
  contact_type: 'Marketing' | 'Finance' | 'Legal' | 'Logistics' | 'Vendor' | 'Other';
  linked_brand_ids?: string[];
  linked_campaign_ids?: string[];
}

export function useContacts() {
  const { currentOrganization } = useOrganization();

  return useQuery({
    queryKey: ['contacts', currentOrganization?.id],
    queryFn: async () => {
      if (!currentOrganization?.id) {
        throw new Error('No organization selected');
      }

      const { data, error } = await supabase
        .from('contacts')
        .select('*')
        .order('created_at', { ascending: false });
      
      if (error) throw error;
      return data as Contact[];
    },
    enabled: !!currentOrganization?.id,
  });
}

export function useCreateContact() {
  const queryClient = useQueryClient();
  const { currentOrganization } = useOrganization();

  return useMutation({
    mutationFn: async (contactData: ContactFormData) => {
      console.log('Creating contact with organization:', currentOrganization?.id);
      
      if (!currentOrganization?.id) {
        throw new Error('No organization selected');
      }

      // Get current user
      const { data: { user } } = await supabase.auth.getUser();
      
      if (!user) {
        throw new Error('User not authenticated');
      }

      const contactWithOrgAndUser = {
        ...contactData,
        organization_id: currentOrganization.id,
        created_by: user.id,
      };

      console.log('Contact data with org and user:', contactWithOrgAndUser);

      const { data, error } = await supabase
        .from('contacts')
        .insert(contactWithOrgAndUser)
        .select()
        .single();

      if (error) {
        console.error('Error creating contact:', error);
        throw error;
      }
      
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['contacts'] });
      toast({
        title: "Success",
        description: "Contact created successfully",
      });
    },
    onError: (error) => {
      console.error('Contact creation failed:', error);
      toast({
        title: "Error",
        description: "Failed to create contact",
        variant: "destructive",
      });
    },
  });
}

export function useUpdateContact() {
  const queryClient = useQueryClient();
  const { currentOrganization } = useOrganization();

  return useMutation({
    mutationFn: async ({ id, data }: { id: string; data: Partial<ContactFormData> }) => {
      if (!currentOrganization?.id) {
        throw new Error('No organization selected');
      }

      const { data: updatedData, error } = await supabase
        .from('contacts')
        .update(data)
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;
      return updatedData;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['contacts'] });
      toast({
        title: "Success",
        description: "Contact updated successfully",
      });
    },
    onError: (error) => {
      toast({
        title: "Error",
        description: "Failed to update contact",
        variant: "destructive",
      });
      console.error('Error updating contact:', error);
    },
  });
}

export function useDeleteContact() {
  const queryClient = useQueryClient();
  const { currentOrganization } = useOrganization();

  return useMutation({
    mutationFn: async (id: string) => {
      if (!currentOrganization?.id) {
        throw new Error('No organization selected');
      }

      const { error } = await supabase
        .from('contacts')
        .delete()
        .eq('id', id);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['contacts'] });
      toast({
        title: "Success",
        description: "Contact deleted successfully",
      });
    },
    onError: (error) => {
      toast({
        title: "Error",
        description: "Failed to delete contact",
        variant: "destructive",
      });
      console.error('Error deleting contact:', error);
    },
  });
}
